import React from "react";
import { useNavigate } from "react-router-dom";
import PlacesAutocomplete from "react-places-autocomplete";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Container,
  Button,
  FormGroup,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Avatar,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { updateHomeDetails } from "src/actions/HomeActions";
import { AMENITIES_LIST, RULES_LIST, COUNTRIES } from "src/utils/misc";

const getItemName = (itemsList) => itemsList?.map((item) => item.name);

const TOKENS = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: TOKENS.bg,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  /* Sticky header bar */
  pageHeaderBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#0F172A",
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#64748B",
    marginTop: 2,
  },
  headerActions: {
    display: "flex",
    gap: 10,
  },
  cancelButton: {
    color: "#64748B",
    borderColor: "#E2E8F0",
    borderRadius: 8,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      borderColor: "#94A3B8",
      backgroundColor: "#F8FAFC",
    },
  },
  saveButton: {
    backgroundColor: "#222222",
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "8px 24px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": {
      backgroundColor: "#000000",
    },
    "&:disabled": {
      backgroundColor: "#C7D2FE",
      color: "#FFFFFF",
    },
  },

  /* Host info */
  hostBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px",
    backgroundColor: "#F8FAFC",
    borderRadius: 10,
    marginBottom: 20,
    border: "1px solid #EEF0F5",
  },
  hostAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },

  /* Sections */
  section: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
    border: "1px solid #EEF0F5",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#0F172A",
  },
  sectionSubtitle: {
    fontSize: 13,
    color: "#64748B",
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },

  /* Form fields */
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: "#222222" },
    },
    "& .MuiInputLabel-root": {
      fontSize: 14,
      color: "#64748B",
    },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5B4FC" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#222222",
    },
  },

  /* Chips */
  chipSelected: {
    backgroundColor: "#222222 !important",
    color: "#FFFFFF !important",
    borderColor: "#222222 !important",
    fontWeight: 500,
  },
  chipUnselected: {
    backgroundColor: "#FFFFFF",
    color: "#475569",
    borderColor: "#E2E8F0",
    fontWeight: 400,
    "&:hover": {
      backgroundColor: "#F8FAFC",
      borderColor: "#A5B4FC",
    },
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },

  /* Images */
  imageGallery: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  imageWrapper: {
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    width: 120,
    height: 90,
  },
  imageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 10,
  },
  coverBadge: {
    position: "absolute",
    bottom: 6,
    left: 6,
    backgroundColor: "#222222",
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 4,
  },
  addImageBox: {
    width: 120,
    height: 90,
    borderRadius: 10,
    border: "2px dashed #D1D5DB",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#222222",
    },
  },

  /* Location suggestions */
  locationSuggestion: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 8,
    marginTop: 4,
    transition: "background-color 0.15s ease",
  },

  /* Footer actions */
  footerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8,
    paddingTop: 16,
    borderTop: "1px solid #EEF0F5",
  },
}));

function EditHome({ updateHomeDetails, home }) {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();

  const [formState, setFormState] = React.useState({
    title: "",
    home_type: "Entire House",
    homeRules: "",
    description: "",
    price: "",
    homeAmenities: "",
    spaceToUse: "Entire House",
    numberOfGuestsExpected: "",
    bedroomCount: "",
    bedCount: "",
    bathroomCount: "",
    other_details: "",
    district: "",
    checkin_time: "",
    homeCountry: "",
    monthlyDiscount: 0,
    weeklyDiscount: 0,
    minStays: 1,
    maxStays: 356,
  });

  const [location, setLocation] = React.useState(`${home.address}`);
  const [amenities, setAmenities] = React.useState([]);
  const [country, setCountry] = React.useState(home.country);
  const [rules, setRules] = React.useState([]);

  React.useEffect(() => {
    setFormState({
      ...formState,
      ...home,
      dates:
        (home.bookedDates &&
          home.bookedDates.map((d) => moment(d).format("YYYY-MM-DD"))) ||
        [],
      bookedDates: home.bookedDates || [],
    });
    setAmenities(getItemName(home.amenities) || []);
    setRules(getItemName(home.rules) || []);
    // eslint-disable-next-line
  }, [home]);

  const handleDateChange = (D) => {
    const date = moment(D).format("YYYY-MM-DD");
    const isPresent = formState.dates.find((d) => d === date);
    setFormState({
      ...formState,
      dates: formState.dates
        .filter((d) => (isPresent ? date !== d : true))
        .concat(isPresent ? [] : [date]),
      bookedDates: formState.dates
        .filter((d) => (isPresent ? date !== d : true))
        .concat(isPresent ? [] : [date]),
    });
  };

  const toggleAmenity = (name) => {
    setAmenities((prev) =>
      prev.includes(name) ? prev.filter((a) => a !== name) : [...prev, name],
    );
  };

  const toggleRule = (name) => {
    setRules((prev) =>
      prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name],
    );
  };

  return (
    <Container className={classes.root}>
      {/* Page Header */}
      <Box className={classes.pageHeaderBar}>
        <Box>
          <Typography className={classes.pageTitle}>Edit Home</Typography>
          <Typography className={classes.pageSubtitle}>
            {home.title} — {home.country}, {home.district}
          </Typography>
        </Box>
      </Box>

      <Formik
        initialValues={home}
        enableReinitialize
        validationSchema={Yup.object().shape({
          title: Yup.string(),
          description: Yup.string(),
          price: Yup.number(),
          numberOfGuestsExpected: Yup.number(),
          bedroomCount: Yup.string(),
          bedCount: Yup.string(),
          bathroomCount: Yup.string(),
          spaceToUse: Yup.string(),
          other_details: Yup.string(),
          district: Yup.string(),
          checkin_time: Yup.string(),
          minStays: Yup.number(),
          maxStays: Yup.number(),
          monthlyDiscount: Yup.number(),
          weeklyDiscount: Yup.number(),
        })}
        onSubmit={async (values) => {
          const amenities_data = amenities.map((amenity) => ({
            name: amenity,
          }));
          const rules_data = rules.map((rule) => ({ name: rule }));
          const obj = {
            ...values,
            spaceToUse: formState.spaceToUse,
            home_type: formState.home_type,
            address: location,
            availableDates: [],
            amenities: amenities_data,
            country,
            rules: rules_data,
          };
          await updateHomeDetails(obj, home.id, navigate);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Images Section */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>Images</Typography>
                <Typography className={classes.sectionSubtitle}>
                  Cover image and gallery. First image is the cover.
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.imageGallery}>
                  {/* Cover Image */}
                  {home.coverImage_URL && (
                    <Box className={classes.imageWrapper}>
                      <img
                        src={home.coverImage_URL}
                        alt="Cover"
                        className={classes.imageThumb}
                      />
                      <Box className={classes.coverBadge}>Cover</Box>
                    </Box>
                  )}

                  {/* Other Images */}
                  {home.otherImages &&
                    home.otherImages.map((image, idx) => (
                      <Box key={idx} className={classes.imageWrapper}>
                        <img
                          src={image.image_url}
                          alt={`Gallery ${idx + 1}`}
                          className={classes.imageThumb}
                        />
                      </Box>
                    ))}

                  {/* Add Image */}
                  <Box
                    className={classes.addImageBox}
                    onClick={() => navigate("/app/home/updateHomeImage")}
                  >
                    <AddPhotoAlternateIcon
                      style={{ color: "#94A3B8", fontSize: 24 }}
                    />
                    <Typography
                      variant="caption"
                      style={{ color: "#94A3B8", marginTop: 4, fontSize: 11 }}
                    >
                      Add Images
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Basic Information
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Property type, title, and location
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Title"
                      name="title"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.title}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) =>
                          setFormState({
                            ...formState,
                            home_type: e.target.value,
                          })
                        }
                        label="Property Type"
                        defaultValue={home.home_type}
                      >
                        {[
                          "Secondary Unit",
                          "Entire House",
                          "Bed and Breakfast",
                          "Boutique",
                          "Apartment",
                          "Hotel Room",
                        ].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disableClearable
                      fullWidth
                      options={COUNTRIES}
                      onChange={(event, newValue) => setCountry(newValue)}
                      inputValue={country}
                      onInputChange={(event, newInputValue) =>
                        setCountry(newInputValue)
                      }
                      autoHighlight
                      defaultValue={home.country}
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className={classes.textField}
                          variant="outlined"
                          label="Country"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "new-password",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="District"
                      name="district"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.district}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PlacesAutocomplete
                      value={location}
                      onChange={setLocation}
                      onSelect={setLocation}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <div>
                          <TextField
                            {...getInputProps({
                              variant: "outlined",
                              label: "Google Maps Location",
                              placeholder: "Search for location",
                              fullWidth: true,
                              className: classes.textField,
                            })}
                          />
                          {(loading || suggestions.length > 0) && (
                            <Box
                              style={{
                                border: "1px solid #E2E8F0",
                                borderRadius: 8,
                                marginTop: 4,
                                overflow: "hidden",
                              }}
                            >
                              {loading && (
                                <Box style={{ padding: 12 }}>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Searching...
                                  </Typography>
                                </Box>
                              )}
                              {suggestions.map((suggestion) => (
                                <div
                                  {...getSuggestionItemProps(suggestion, {
                                    className: classes.locationSuggestion,
                                    style: {
                                      backgroundColor: suggestion.active
                                        ? "#F1F5F9"
                                        : "#FFFFFF",
                                    },
                                  })}
                                >
                                  <LocationCityIcon
                                    style={{
                                      marginRight: 10,
                                      color: "#94A3B8",
                                      fontSize: 18,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    style={{ fontSize: 13 }}
                                  >
                                    {suggestion.description}
                                  </Typography>
                                </div>
                              ))}
                            </Box>
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Capacity */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Capacity
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  How many guests can your space accommodate?
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Max Guests"
                      name="numberOfGuestsExpected"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.numberOfGuestsExpected}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Bedrooms"
                      name="bedroomCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.bedroomCount}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Beds"
                      name="bedCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.bedCount}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Bathrooms"
                      name="bathroomCount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.bathroomCount}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Description
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Tell guests about the space
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={3}
                      label="Full Description"
                      name="description"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.description}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>What will guests have?</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) =>
                          setFormState({
                            ...formState,
                            spaceToUse: e.target.value,
                          })
                        }
                        label="What will guests have?"
                        defaultValue={home.spaceToUse}
                      >
                        {[
                          "Entire House",
                          "Private Room",
                          "Shared Room",
                          "All Apartment",
                        ].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Other details"
                      name="other_details"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.other_details}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Amenities
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Select what your home offers —{" "}
                  {amenities.length > 0
                    ? `${amenities.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {AMENITIES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={amenities.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        amenities.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleAmenity(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* House Rules */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  House Rules
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Set expectations for guests —{" "}
                  {rules.length > 0
                    ? `${rules.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {RULES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={rules.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        rules.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleRule(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Pricing
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Nightly rate and check-in details
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Price per night (USD)"
                      name="price"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.price}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Check-in Time"
                      name="checkin_time"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="e.g. Flexible, 2:00 PM"
                      defaultValue={home.checkin_time}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Stay Duration & Discounts */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Stay Duration & Discounts
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Minimum/maximum nights and length-of-stay discounts
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 1 } }}
                      label="Min Nights"
                      name="minStays"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.minStays}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 1 } }}
                      label="Max Nights"
                      name="maxStays"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.maxStays}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Weekly Discount (%)"
                      name="weeklyDiscount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.weeklyDiscount}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      label="Monthly Discount (%)"
                      name="monthlyDiscount"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      defaultValue={home.monthlyDiscount}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Calendar */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Availability Calendar
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Select dates you're booked or unavailable
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <SingleDatePicker
                  placeholder="Select dates"
                  block={false}
                  numberOfMonths={1}
                  onDateChange={handleDateChange}
                  focused={formState.calendarFocused}
                  onFocusChange={() =>
                    setFormState({
                      ...formState,
                      calendarFocused: !formState.calendarFocused,
                    })
                  }
                  keepOpenOnDateSelect={true}
                  isDayHighlighted={(d1) =>
                    formState.dates &&
                    formState.dates.some((d2) => d1.isSame(d2, "day"))
                  }
                />
              </CardContent>
            </Card>

            {/* Footer Actions */}
            <Box className={classes.footerActions}>
              <Button
                variant="outlined"
                className={classes.cancelButton}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={classes.saveButton}
              >
                Update Home
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Container>
  );
}

const mapStateToProps = (state) => ({
  home: state.homes.home,
});

export default connect(mapStateToProps, { updateHomeDetails })(EditHome);
